/**
 * Authentication utility functions for better session management
 */

import {
  IPublicClientApplication,
  AccountInfo,
  SilentRequest,
} from '@azure/msal-browser';

/**
 * Attempts to restore user session by trying silent token acquisition
 * This helps maintain user sessions across browser restarts
 */
export async function tryRestoreUserSession(
  msalInstance: IPublicClientApplication,
  scopes: string[] = []
): Promise<AccountInfo | null> {
  try {
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
      console.log('No accounts found in cache');
      return null;
    }

    const account = accounts[0]; // Use the first account
    msalInstance.setActiveAccount(account);

    // Try to acquire token silently to verify session validity
    const silentRequest: SilentRequest = {
      account: account,
      forceRefresh: true,
      scopes: scopes.length > 0 ? scopes : ['openid', 'profile'],
    };

    try {
      const response = await msalInstance.acquireTokenSilent(silentRequest);
      console.log('Silent token acquisition successful - session restored');
      return response.account;
    } catch (silentError) {
      // Silent token acquisition failed, but account might still be valid
      console.log(
        'Silent token acquisition failed, but account is available:',
        silentError
      );

      // Check if it's an interaction required error
      if (
        silentError &&
        typeof silentError === 'object' &&
        'errorCode' in silentError
      ) {
        const errorCode = (silentError as { errorCode: string }).errorCode;
        if (
          errorCode === 'interaction_required' ||
          errorCode === 'consent_required'
        ) {
          console.log('User interaction required for token refresh');
          // Account is still valid, just needs user interaction for new tokens
          return account;
        }
      }

      // For other errors, account might still be usable
      return account;
    }
  } catch (error) {
    console.error('Error during session restore:', error);
    return null;
  }
}

/**
 * Checks if the user has a valid session without requiring interaction
 */
export function hasValidSession(
  msalInstance: IPublicClientApplication
): boolean {
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0;
}

/**
 * Gets the active account or the first available account
 */
export function getActiveOrFirstAccount(
  msalInstance: IPublicClientApplication
): AccountInfo | null {
  const activeAccount = msalInstance.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
    return accounts[0];
  }

  return null;
}

/**
 * Clears the user session (logout)
 */
export async function clearUserSession(
  msalInstance: IPublicClientApplication
): Promise<void> {
  try {
    let account = msalInstance.getActiveAccount();
    const allAccounts = msalInstance.getAllAccounts();
    // If no active account but only one account exists, use it
    if (!account && allAccounts.length === 1) {
      account = allAccounts[0];
    }
    if (account) {
      await msalInstance.logoutRedirect({
        account: account,
        postLogoutRedirectUri: '/',
      });
    } else {
      // No account, just logout
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: '/',
      });
    }
  } catch (error) {
    console.error('Error during logout:', error);
    // Fallback: clear the cache manually
    msalInstance.clearCache();
  }
}

/**
 * Gets API scopes from the current auth configuration
 */
export function getApiScopes(): string[] {
  // This should match your backend API scopes
  // You can make this dynamic by reading from your auth config
  return [];
}
